import * as Schema from "effect/Schema";

const Text = Schema.String.check(Schema.isNonEmpty());
const Seconds = Schema.Number.check(Schema.isGreaterThanOrEqualTo(0));
const Count = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const Score = Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 100 }));
const limit = (max: number) =>
  Schema.Int.check(Schema.isGreaterThan(0)).check(Schema.isLessThanOrEqualTo(max));
const Day = Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/));
const Id = Text.check(Schema.isMaxLength(200));
const Nullable = Schema.NullOr;

export const HeydayCategory = Schema.Literals([
  "politics",
  "world",
  "conflict",
  "justice",
  "disasters",
  "economy",
  "business",
  "energy",
  "tech",
  "science",
  "health",
  "culture",
  "sports",
]);
export const HeydayReportId = Text.check(
  Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
);
export const HeydayKind = Schema.Literals([
  "report",
  "story",
  "source",
  "claim",
  "entity",
  "situation",
  "outlet",
]);
export type HeydayKind = typeof HeydayKind.Type;
const EntityType = Schema.Literals(["person", "place", "org"]);
const StoryRelation = Schema.Literals([
  "same_event",
  "follow_up",
  "continues",
  "background",
  "contradicts",
  "sibling",
]);
const ClaimStatus = Schema.Literals(["corroborated", "contradicted", "unresolved", "uncompared"]);
/** What kind of statement a claim is, as HeyDay's record sorts the newsroom's free-text labels. */
export const HeydayClaimType = Schema.Literals([
  "statement",
  "response",
  "allegation",
  "event",
  "action",
  "legal",
  "investigation",
  "figure",
  "forecast",
  "plan",
  "analysis",
  "context",
  "other",
]);

// Records. Every kind has a compact summary (lists, search) and a detail (get).
// `ref` is the link that names a record in an answer: its best public URL with a
// `#heyday-<kind>=<id>` fragment, so it opens the real page anywhere and the app
// can draw the record it names. A record arrives ready to show: `media` is the
// picture its card leads with and the video to play, and a detail carries what
// its card draws.

/** The picture a record's card leads with, and the video and second to play where it has one. */
const Media = Schema.Struct({
  image: Nullable(Text),
  video: Nullable(Schema.Struct({ id: Text, seconds: Seconds })),
});
export type HeydayMedia = typeof Media.Type;
/** An entity as another record names it. `imageUrl` is its picture, for a resolved person or organisation that has one. */
const EntityMention = Schema.Struct({
  id: Text,
  type: EntityType,
  name: Text,
  imageUrl: Nullable(Text),
  /** Where a named place is, when HeyDay has resolved it. */
  latitude: Schema.optional(Nullable(Schema.Number)),
  longitude: Schema.optional(Nullable(Schema.Number)),
});
/** Where in an outlet's video a claim was made. `estimated` when placed from the transcript's length, not its caption timings. */
const Moment = Schema.Struct({ seconds: Seconds, url: Text, estimated: Schema.Boolean });
/**
 * Who stands behind a claim. `independent` counts the separate accounts of it
 * (this one included), `outlets` every outlet that carried it, `echoes` the
 * outlets that only repeated another's report, `contradictedBy` the outlets
 * whose claim cannot also be true.
 */
const Witnesses = Schema.Struct({
  independent: Count,
  outlets: Count,
  echoes: Count,
  contradictedBy: Count,
  firstOutlet: Text,
  firstAt: Nullable(Text),
});
const DataPoint = Schema.Struct({
  label: Text,
  value: Text,
  description: Nullable(Schema.String),
});
const LinkedStory = Schema.Struct({
  storyId: Text,
  relation: StoryRelation,
  headline: Nullable(Schema.String),
});

export const HeydayReportSummary = Schema.Struct({
  kind: Schema.Literal("report"),
  id: HeydayReportId,
  url: Text,
  ref: Text,
  title: Text,
  airedAt: Nullable(Text),
  desk: Nullable(HeydayCategory),
  kicker: Nullable(Schema.String),
  origin: Schema.Literals(["run", "youtube"]),
  storyId: Nullable(Text),
  situationId: Nullable(Text),
  videoId: Nullable(Text),
  articleUrl: Nullable(Text),
  dek: Nullable(Schema.String),
  sourceCount: Count,
  outletCount: Count,
  media: Media,
});
export type HeydayReportSummary = typeof HeydayReportSummary.Type;
export const HeydayCitation = Schema.Struct({
  number: Count,
  title: Text,
  url: Text,
  outlet: Nullable(Schema.String),
  publishedAt: Nullable(Schema.String),
});
export const HeydayStorySummary = Schema.Struct({
  kind: Schema.Literal("story"),
  id: Text,
  url: Nullable(Text),
  ref: Text,
  headline: Text,
  claim: Schema.String,
  state: Schema.Literals(["active", "retired"]),
  aired: Schema.Boolean,
  desk: Nullable(HeydayCategory),
  firstSourceAt: Nullable(Text),
  lastSourceAt: Nullable(Text),
  sourceCount: Count,
  outletCount: Count,
  outlets: Schema.Array(Text),
  reportIds: Schema.Array(Text),
  situationId: Nullable(Text),
  media: Media,
});
export const HeydaySourceSummary = Schema.Struct({
  kind: Schema.Literal("source"),
  id: Text,
  ref: Text,
  outlet: Text,
  outletId: Text,
  title: Text,
  url: Text,
  medium: Schema.Literals(["video", "article"]),
  publishedAt: Nullable(Text),
  headline: Text,
  coreClaim: Schema.String,
  storyId: Nullable(Text),
  decision: Nullable(Schema.Literals(["used", "ignored", "needs_more_sources"])),
  gravity: Nullable(Schema.Literals(["routine", "notable", "major", "extreme"])),
  claimCount: Count,
  media: Media,
  /** Set on a document from the user's own library: where it is kept and what they noted about it. */
  own: Schema.optional(
    Schema.Struct({ path: Nullable(Schema.String), note: Nullable(Schema.String) }),
  ),
});
export type HeydaySourceSummary = typeof HeydaySourceSummary.Type;
export const HeydayClaimSummary = Schema.Struct({
  kind: Schema.Literal("claim"),
  id: Text,
  ref: Text,
  text: Text,
  type: HeydayClaimType,
  materiality: Score,
  certainty: Score,
  attribution: Nullable(Schema.String),
  occurredAt: Nullable(Text),
  dueAt: Nullable(Text),
  outlet: Text,
  outletId: Text,
  sourceId: Text,
  sourceUrl: Text,
  publishedAt: Nullable(Text),
  storyId: Nullable(Text),
  status: ClaimStatus,
  point: Nullable(Schema.String),
  witnesses: Witnesses,
  moment: Nullable(Moment),
  media: Media,
  /** Set on a claim from the user's own library: the document's own words and where in it they stand. */
  own: Schema.optional(
    Schema.Struct({ quote: Nullable(Schema.String), locator: Nullable(Schema.String) }),
  ),
});
export type HeydayClaimSummary = typeof HeydayClaimSummary.Type;
export const HeydayEntitySummary = Schema.Struct({
  kind: Schema.Literal("entity"),
  id: Text,
  ref: Text,
  type: EntityType,
  name: Text,
  description: Nullable(Schema.String),
  imageUrl: Nullable(Text),
  resolved: Schema.Boolean,
  wikidataId: Nullable(Text),
  googlePlaceId: Nullable(Text),
  countryCode: Nullable(Schema.String),
  latitude: Nullable(Schema.Number),
  longitude: Nullable(Schema.Number),
  reportCount: Count,
  sourceCount: Count,
  claimCount: Count,
  media: Media,
});
export const HeydaySituationSummary = Schema.Struct({
  kind: Schema.Literal("situation"),
  id: Text,
  url: Text,
  ref: Text,
  name: Text,
  subject: Schema.String,
  status: Schema.Literals(["open", "resolved", "quiet"]),
  reportCount: Count,
  firstAiredAt: Nullable(Text),
  lastAiredAt: Nullable(Text),
  revisitOn: Nullable(Text),
  expecting: Nullable(Schema.String),
  media: Media,
});
/** Who published a source, as one identity however its channel, feed and site are named. */
export const HeydayOutletSummary = Schema.Struct({
  kind: Schema.Literal("outlet"),
  id: Text,
  ref: Text,
  name: Text,
  url: Nullable(Text),
  host: Nullable(Schema.String),
  names: Schema.Array(Text),
  sourceCount: Count,
  claimCount: Count,
  firstSourceAt: Nullable(Text),
  lastSourceAt: Nullable(Text),
  media: Media,
});

/**
 * A forward-looking claim and what became of it. `outcome` is what HeyDay's own
 * follow-up found; `resolved` is what this installation established, which
 * HeyDex adds and HeyDay never sends.
 */
const DuePromise = Schema.Struct({
  claim: HeydayClaimSummary,
  promiseType: Nullable(Schema.String),
  actor: Nullable(Schema.String),
  condition: Nullable(Schema.String),
  overdue: Schema.Boolean,
  outcome: Nullable(
    Schema.Struct({
      outcome: Schema.Literals(["kept", "broken", "partly_kept", "delayed", "unresolved"]),
      reportId: Nullable(Text),
      decidedAt: Nullable(Text),
    }),
  ),
  resolved: Schema.optional(
    Schema.Struct({
      outcome: Schema.Literals(["kept", "broken", "partly", "superseded"]),
      summary: Schema.String,
      evidence: Schema.Array(Text),
      recordedAt: Text,
    }),
  ),
});
export type HeydayDuePromise = typeof DuePromise.Type;
const Promises = Schema.Struct({ total: Count, items: Schema.Array(DuePromise) });

const CoverageCell = Schema.Struct({
  claimId: Text,
  ref: Text,
  outlet: Text,
  outletId: Text,
  text: Text,
  publishedAt: Nullable(Text),
  sourceUrl: Text,
  first: Schema.Boolean,
  echo: Schema.Boolean,
});
export const HeydayCoverage = Schema.Struct({
  storyId: Text,
  headline: Text,
  outlets: Schema.Array(Schema.Struct({ outlet: Text, firstReportedAt: Nullable(Text) })),
  points: Schema.Array(
    Schema.Struct({
      title: Text,
      status: Schema.Literals(["corroborated", "contradicted", "unresolved"]),
      claims: Schema.Array(CoverageCell),
    }),
  ),
  contradictions: Schema.Array(
    Schema.Struct({ a: CoverageCell, b: CoverageCell, reason: Nullable(Schema.String) }),
  ),
  ungroupedClaims: Count,
  asOf: Nullable(Text),
});
export type HeydayCoverage = typeof HeydayCoverage.Type;
/** Where a story's outlets disagreed, now or as the record stood at a moment: the part of its coverage a card draws. */
export const HeydayDisputes = Schema.Struct({
  storyId: Text,
  asOf: Nullable(Text),
  contradictions: HeydayCoverage.fields.contradictions,
});
export type HeydayDisputes = typeof HeydayDisputes.Type;

/** One account arriving in a story, and what was on the record once it had. */
const TimelineStep = Schema.Struct({
  at: Nullable(Text),
  outlet: Text,
  outletId: Nullable(Text),
  /** The outlet's icon, where the record holds one. */
  outletIcon: Nullable(Text),
  sourceId: Text,
  title: Text,
  url: Text,
  media: Nullable(Media),
  outlets: Count,
  sources: Count,
  claims: Count,
  corroborated: Count,
  contradicted: Count,
  opened: Schema.Array(Schema.String),
  /** A HeyDay report on the story cites this account. */
  cited: Schema.Boolean,
});
export type HeydayTimelineStep = typeof TimelineStep.Type;
/** An identity a report is tagged with: its part in the report, and whether it is the place the report is mainly about. */
const ReportMention = Schema.Struct({
  ...EntityMention.fields,
  role: Nullable(Schema.String),
  primary: Schema.Boolean,
});
/** Pages a report cites, by who published them. */
const CitedOutlet = Schema.Struct({
  outlet: Text,
  sources: Schema.Array(Schema.Struct({ title: Text, url: Text })),
});
/**
 * A HeyDay report as its story carries it: what a page that shows the two
 * together draws of the report. `documents` is what it cites beyond the
 * story's accounts: what the newsroom looked up itself.
 */
const StoryReport = Schema.Struct({
  ...HeydayReportSummary.fields,
  entities: Schema.Array(ReportMention),
  documents: Schema.Array(CitedOutlet),
});
export type HeydayStoryReport = typeof StoryReport.Type;
const HeydayStory = Schema.Struct({
  ...HeydayStorySummary.fields,
  summary: Nullable(Schema.String),
  keyPoints: Schema.Array(Schema.String),
  openQuestions: Schema.Array(Schema.String),
  /** The identities its accounts name, each with the accounts that name it, oldest first. */
  entities: Schema.Array(
    Schema.Struct({ ...EntityMention.fields, sourceCount: Count, sourceIds: Schema.Array(Text) }),
  ),
  related: Schema.Array(LinkedStory),
  /** HeyDay's reports on the story, oldest first. */
  reports: Schema.Array(StoryReport),
  /** The story replayed account by account, oldest first. */
  timeline: Schema.Array(TimelineStep),
  claimCount: Count,
  pointCount: Count,
  /** The cross-outlet comparison, when it was asked for with the story. */
  coverage: Schema.optional(HeydayCoverage),
  /** The user's own documents about the story, from their library. HeyDay never sends this. */
  own: Schema.optional(Schema.Array(HeydaySourceSummary)),
});
export type HeydayStory = typeof HeydayStory.Type;
export const HeydayReport = Schema.Struct({
  ...HeydayReportSummary.fields,
  entities: Schema.Array(ReportMention),
  related: Schema.Array(LinkedStory),
  writeUp: Nullable(
    Schema.Struct({
      text: Schema.String,
      citations: Schema.Array(HeydayCitation),
      truncated: Schema.Boolean,
      nextOffset: Nullable(Count),
      totalCharacters: Count,
    }),
  ),
  /** What the write-up cites, by outlet. */
  outlets: Schema.Array(CitedOutlet),
  transcript: Nullable(
    Schema.Struct({
      lines: Schema.Array(Schema.Struct({ startSeconds: Seconds, text: Text, momentUrl: Text })),
      truncated: Schema.Boolean,
      nextFromSeconds: Nullable(Seconds),
      totalLines: Count,
    }),
  ),
  /** The story it reports, when it was asked for with the report. */
  story: Nullable(HeydayStory),
});
export type HeydayReport = typeof HeydayReport.Type;
const HeydaySource = Schema.Struct({
  ...HeydaySourceSummary.fields,
  summary: Schema.String,
  topics: Schema.Array(Schema.String),
  dataPoints: Schema.Array(DataPoint),
  entities: Schema.Array(EntityMention),
  alsoCovers: Schema.Array(Text),
  claims: Schema.Array(HeydayClaimSummary),
});
/**
 * One account in a claim's chain of custody. `role` is where it stands: said it
 * first, reported it separately, only repeated another's report, or cannot also
 * be true. `reason` is one sentence on how it relates to the claim; the claim's
 * own entry has none.
 */
const Account = Schema.Struct({
  claimId: Text,
  ref: Text,
  role: Schema.Literals(["origin", "independent", "echo", "contradicts"]),
  relation: Nullable(Schema.Literals(["same", "agrees", "refines", "contradicts"])),
  outlet: Text,
  outletId: Text,
  text: Text,
  publishedAt: Nullable(Text),
  sourceUrl: Text,
  moment: Nullable(Moment),
  media: Media,
  reason: Nullable(Schema.String),
});
export type HeydayAccount = typeof Account.Type;
const HeydayClaim = Schema.Struct({
  ...HeydayClaimSummary.fields,
  storyHeadline: Nullable(Schema.String),
  entities: Schema.Array(EntityMention),
  dataPoints: Schema.Array(DataPoint),
  promise: Nullable(
    Schema.Struct({
      type: Schema.Literals(["promise", "deadline", "forecast", "scheduled", "other"]),
      actor: Nullable(Schema.String),
      condition: Nullable(Schema.String),
      outcome: DuePromise.fields.outcome,
    }),
  ),
  /** Everyone who stands behind the claim or against it, oldest first, this claim included. */
  accounts: Schema.Array(Account),
});
const HeydayEntity = Schema.Struct({
  ...HeydayEntitySummary.fields,
  mentionedWith: Schema.Array(Schema.Struct({ ...EntityMention.fields, sharedSources: Count })),
  recentReports: Schema.Array(HeydayReportSummary),
  recentStories: Schema.Array(HeydayStorySummary),
  promises: Promises,
  /** What the user's own documents say about them, from their library. HeyDay never sends this. */
  own: Schema.optional(Schema.Array(HeydayClaimSummary)),
});
const HeydaySituation = Schema.Struct({
  ...HeydaySituationSummary.fields,
  includes: Schema.String,
  excludes: Schema.String,
  endsWhen: Schema.String,
  standing: Nullable(Schema.String),
  reports: Schema.Array(
    Schema.Struct({ id: Text, url: Text, title: Text, airedAt: Nullable(Text) }),
  ),
  alsoCoveredBy: Schema.Array(Text),
  checks: Schema.Array(
    Schema.Struct({
      checkedAt: Text,
      outcome: Schema.Literals(["development", "resolved", "quiet"]),
      summary: Schema.String,
      eventAt: Nullable(Text),
      nextOn: Nullable(Text),
      sources: Schema.Array(Schema.Struct({ title: Nullable(Schema.String), url: Text })),
    }),
  ),
  promises: Promises,
});
const HeydayOutlet = Schema.Struct({
  ...HeydayOutletSummary.fields,
  /** How its claims stand against other outlets' accounts. */
  standing: Schema.Struct({
    corroborated: Count,
    contradicted: Count,
    unresolved: Count,
    uncompared: Count,
  }),
  recentSources: Schema.Array(HeydaySourceSummary),
});
export const HeydayRecord = Schema.Union([
  HeydayReport,
  HeydayStory,
  HeydaySource,
  HeydayClaim,
  HeydayEntity,
  HeydaySituation,
  HeydayOutlet,
]);
export type HeydayRecord = typeof HeydayRecord.Type;
/** A tool result must be an object; a record is one of seven. */
export const HeydayGetResult = Schema.Struct({ record: HeydayRecord });

// Requests. One filter vocabulary serves list, count and export; the API answers a
// filter that does not apply to the kind with an invalid-request error naming the
// ones that do.

const filters = {
  q: Schema.optional(Text.check(Schema.isMaxLength(500))),
  from: Schema.optional(Day),
  to: Schema.optional(Day),
  dateField: Schema.optional(Schema.Literals(["published", "occurred", "due"])),
  desk: Schema.optional(HeydayCategory),
  entity: Schema.optional(Id),
  story: Schema.optional(Id),
  situation: Schema.optional(Id),
  source: Schema.optional(Id),
  outlet: Schema.optional(Text.check(Schema.isMaxLength(200))),
  status: Schema.optional(
    Schema.Literals([...ClaimStatus.literals, "open", "resolved", "quiet", "active", "retired"]),
  ),
  type: Schema.optional(Text.check(Schema.isMaxLength(80))),
  decision: Schema.optional(Schema.Literals(["used", "ignored", "needs_more_sources"])),
  gravity: Schema.optional(Schema.Literals(["routine", "notable", "major", "extreme"])),
  aired: Schema.optional(Schema.Boolean),
  resolved: Schema.optional(Schema.Boolean),
  minMateriality: Schema.optional(Score),
  minOutlets: Schema.optional(limit(1000)),
};
export const HeydaySearchInput = Schema.Struct({
  q: Text.check(Schema.isMaxLength(500)),
  kinds: Schema.optional(Schema.Array(HeydayKind)),
  from: Schema.optional(Day),
  to: Schema.optional(Day),
  desk: Schema.optional(HeydayCategory),
  perKind: Schema.optional(limit(20)),
});
export const HeydayListInput = Schema.Struct({
  kind: HeydayKind,
  ...filters,
  sort: Schema.optional(Schema.Literals(["newest", "oldest", "relevance", "weight"])),
  limit: Schema.optional(limit(50)),
  cursor: Schema.optional(Text.check(Schema.isMaxLength(64))),
});
const Instant = Text.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/));
export const HeydayGetInput = Schema.Struct({
  kind: HeydayKind,
  id: Id,
  includeTranscript: Schema.optional(Schema.Boolean),
  fromSeconds: Schema.optional(Seconds),
  maxTranscriptLines: Schema.optional(limit(200)),
  writeUpOffset: Schema.optional(Count),
  maxWriteUpCharacters: Schema.optional(limit(16000)),
  /** For a report: bring the story it reports with it. */
  includeStory: Schema.optional(Schema.Boolean),
  /** For a story: also compare what its outlets said, now or as the record stood at `asOf`. */
  coverage: Schema.optional(Schema.Boolean),
  asOf: Schema.optional(Instant),
});
export const HeydayCoverageInput = Schema.Struct({ story: Id, asOf: Schema.optional(Instant) });
const scope = {
  story: Schema.optional(Id),
  entity: Schema.optional(Id),
  situation: Schema.optional(Id),
};
/** `since` is a date, a time, or the cursor an earlier answer returned. */
export const HeydayChangesInput = Schema.Struct({
  since: Text.check(Schema.isMaxLength(120)),
  ...scope,
  limit: Schema.optional(limit(200)),
});
export const HeydayCountInput = Schema.Struct({
  kind: HeydayKind,
  by: Schema.Literals([
    "desk",
    "day",
    "week",
    "outlet",
    "type",
    "status",
    "decision",
    "gravity",
    "medium",
    "origin",
    "entity",
    "story",
    "country",
  ]),
  ...filters,
  limit: Schema.optional(limit(100)),
});
/** A whole selection as flat rows, for loading into a local database. */
export const HeydayExportInput = Schema.Struct({
  kind: HeydayKind,
  ...filters,
  columns: Schema.optional(Schema.Array(Text.check(Schema.isMaxLength(40)))),
});
/** A window of days, or everything due for a story, an identity, a situation or an actor. */
export const HeydayCalendarInput = Schema.Struct({
  from: Schema.optional(Day),
  to: Schema.optional(Day),
  ...scope,
  actor: Schema.optional(Text.check(Schema.isMaxLength(200))),
  limit: Schema.optional(limit(200)),
});

// Responses.

export const HeydayGuide = Schema.Struct({
  asOf: Text,
  counts: Schema.Record(HeydayKind, Count),
  guide: Text,
});
const SearchHit = Schema.Struct({
  id: Text,
  ref: Nullable(Text),
  title: Text,
  snippet: Nullable(Schema.String),
  date: Nullable(Text),
  url: Nullable(Text),
  image: Nullable(Text),
  /** Whether the question's words, its meaning or both found the record. */
  match: Schema.optional(Schema.Literals(["words", "meaning", "both"])),
  /** True for a record from the user's own library. */
  own: Schema.optional(Schema.Boolean),
});
export const HeydaySearchResult = Schema.Struct({
  query: Text,
  matched: Schema.Literals(["all", "any"]),
  /** Whether HeyDay's record was also searched by meaning. */
  meaning: Schema.optional(Schema.Boolean),
  results: Schema.Array(
    Schema.Struct({ kind: HeydayKind, total: Count, hits: Schema.Array(SearchHit) }),
  ),
});
export const HeydayListResult = Schema.Struct({
  kind: HeydayKind,
  total: Count,
  next: Nullable(Text),
  items: Schema.Array(
    Schema.Union([
      HeydayReportSummary,
      HeydayStorySummary,
      HeydaySourceSummary,
      HeydayClaimSummary,
      HeydayEntitySummary,
      HeydaySituationSummary,
      HeydayOutletSummary,
    ]),
  ),
});
export const HeydayChange = Schema.Union([
  Schema.Struct({ type: Schema.Literal("source"), at: Text, source: HeydaySourceSummary }),
  Schema.Struct({ type: Schema.Literal("report"), at: Text, report: HeydayReportSummary }),
  Schema.Struct({
    type: Schema.Literals(["corroboration", "contradiction"]),
    at: Text,
    a: CoverageCell,
    b: CoverageCell,
  }),
  Schema.Struct({ type: Schema.Literal("promise_due"), at: Text, claim: HeydayClaimSummary }),
  Schema.Struct({
    type: Schema.Literal("situation_check"),
    at: Text,
    situationId: Text,
    name: Text,
    outcome: Schema.Literals(["development", "resolved", "quiet"]),
    summary: Schema.String,
  }),
]);
export type HeydayChange = typeof HeydayChange.Type;
/** What moved on the record after `since`. Pass `cursor` as the next `since`. */
export const HeydayChanges = Schema.Struct({
  since: Text,
  cursor: Text,
  total: Count,
  items: Schema.Array(HeydayChange),
});
export type HeydayChanges = typeof HeydayChanges.Type;
export const HeydayCountResult = Schema.Struct({
  kind: HeydayKind,
  by: Text,
  total: Count,
  groups: Schema.Array(
    Schema.Struct({ value: Nullable(Schema.String), label: Nullable(Schema.String), count: Count }),
  ),
});
export const HeydayTable = Schema.Struct({
  kind: HeydayKind,
  columns: Schema.Array(Text),
  rows: Schema.Array(Schema.Array(Nullable(Schema.Union([Schema.String, Schema.Number])))),
  total: Count,
  next: Nullable(Text),
});
export const HeydayCalendar = Schema.Struct({
  from: Nullable(Day),
  to: Nullable(Day),
  total: Count,
  items: Schema.Array(
    Schema.Union([
      Schema.Struct({ type: Schema.Literal("claim_due"), date: Text, ...DuePromise.fields }),
      Schema.Struct({
        type: Schema.Literal("situation_check"),
        date: Text,
        situation: HeydaySituationSummary,
      }),
    ]),
  ),
});

// Recipes. A recipe is a way of working the record that HeyDex knows by name: the
// prompt box offers them, and the agent asks for the steps of the one it was given.

export const HeyDexRecipeName = Schema.Literals([
  "timeline",
  "first",
  "disagreements",
  "document",
  "folder",
  "brief",
]);
export type HeyDexRecipeName = typeof HeyDexRecipeName.Type;
/** The recipes as the prompt box lists them. */
export const HEYDEX_RECIPES: ReadonlyArray<{
  readonly name: HeyDexRecipeName;
  readonly description: string;
}> = [
  { name: "timeline", description: "What happened, in order, each date cited" },
  { name: "first", description: "Who said it first, who confirmed it, who only repeated it" },
  { name: "disagreements", description: "Where the outlets disagree on a story" },
  { name: "document", description: "Check your own document against the public record" },
  { name: "folder", description: "Read a folder of your documents into your library" },
  { name: "brief", description: "One screen for an editor: known, disputed, unknown, next" },
];
/** What a message opens with to ask for a recipe: the name, then what to run it on. */
export const recipeOpening = (name: HeyDexRecipeName) => `Recipe ${name}: `;
export const HeyDexRecipeInput = Schema.Struct({ name: HeyDexRecipeName });
export const HeyDexRecipe = Schema.Struct({ name: HeyDexRecipeName, steps: Text });

// What HeyDex sends back. Everything a user's research produces is an unverified
// lead to the newsroom: a pointer and its public evidence, never content, and never
// anything from the user's own files.

const PublicUrl = Text.check(Schema.isPattern(/^https?:\/\/\S+$/)).check(Schema.isMaxLength(2048));
export const HeydayRecordRef = Schema.Struct({ kind: HeydayKind, id: Id });
export type HeydayRecordRef = typeof HeydayRecordRef.Type;
const Line = Text.check(Schema.isMaxLength(600));
export const HeydayContribution = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("lead"),
    url: PublicUrl,
    title: Line,
    outlet: Text.check(Schema.isMaxLength(200)),
    publishedAt: Schema.optional(Instant),
    establishes: Line,
    story: Schema.optional(Id),
  }),
  Schema.Struct({
    kind: Schema.Literal("finding"),
    type: Schema.Literals([
      "claim_corrected",
      "claims_agree",
      "claims_contradict",
      "situation_developed",
      "wrong_story",
      "same_event",
      "outlet_alias",
      "entity_identity",
      "report_error",
    ]),
    records: Schema.Array(HeydayRecordRef)
      .check(Schema.isMinLength(1))
      .check(Schema.isMaxLength(8)),
    summary: Line,
    evidence: Schema.Array(PublicUrl).check(Schema.isMaxLength(8)),
  }),
  Schema.Struct({
    kind: Schema.Literal("promise_outcome"),
    claim: Id,
    outcome: Schema.Literals(["kept", "broken", "partly", "superseded"]),
    summary: Line,
    evidence: Schema.Array(PublicUrl).check(Schema.isMinLength(1)).check(Schema.isMaxLength(8)),
  }),
]);
export type HeydayContribution = typeof HeydayContribution.Type;
export const HeydayContributeInput = Schema.Struct({
  items: Schema.Array(HeydayContribution)
    .check(Schema.isMinLength(1))
    .check(Schema.isMaxLength(20)),
});
/** What the intake answers for each item, in order. */
export const HeydayIntakeResult = Schema.Struct({
  receipts: Schema.Array(
    Schema.Struct({ receipt: Text, status: Schema.Literals(["accepted", "duplicate"]) }),
  ),
});
/** One thing this installation sent, as the user sees it under the answer it came from. */
export const HeyDexContribution = Schema.Struct({
  id: Text,
  threadId: Nullable(Text),
  /** The turn whose answer the item is listed under. */
  turnId: Nullable(Text),
  createdAt: Text,
  item: HeydayContribution,
  status: Schema.Literals(["sent", "duplicate", "failed", "retracted"]),
  receipt: Nullable(Text),
  error: Nullable(Schema.String),
});
export type HeyDexContribution = typeof HeyDexContribution.Type;
export const HeyDexContributions = Schema.Struct({ items: Schema.Array(HeyDexContribution) });
export const HeyDexContributionsInput = Schema.Struct({ threadId: Schema.optional(Text) });
export const HeyDexContributionInput = Schema.Struct({ id: Text });

// The library. The user's own material in the record's shape: a document is an
// account (a `source`) and what it says is its claims, so one search and one read
// answer for HeyDay's record and the user's own together. It is one file on this
// machine and nothing in it is ever sent anywhere.

/** An id that names a record of the user's own library, never one of HeyDay's. */
export const OWN_RECORD_PREFIX = "own.";
export const isOwnRecord = (id: string) => id.startsWith(OWN_RECORD_PREFIX);

const LibraryClaim = Schema.Struct({
  text: Text.check(Schema.isMaxLength(1000)),
  type: Schema.optional(HeydayClaimType),
  attribution: Schema.optional(Schema.String.check(Schema.isMaxLength(300))),
  occurredAt: Schema.optional(Schema.String.check(Schema.isMaxLength(40))),
  dueAt: Schema.optional(Schema.String.check(Schema.isMaxLength(40))),
  certainty: Schema.optional(Score),
  materiality: Schema.optional(Score),
  quote: Schema.optional(Schema.String.check(Schema.isMaxLength(2000))),
  locator: Schema.optional(Schema.String.check(Schema.isMaxLength(200))),
  /** The HeyDay identities the claim is about, by id, so each one's record can show it. */
  entities: Schema.optional(Schema.Array(Id).check(Schema.isMaxLength(12))),
});
/** A document of the user's and the claims it makes: `title` adds it, `document` adds to one already there. */
export const HeyDexLibraryAddInput = Schema.Struct({
  title: Schema.optional(Text.check(Schema.isMaxLength(300))),
  path: Schema.optional(Schema.String.check(Schema.isMaxLength(1000))),
  url: Schema.optional(Schema.String.check(Schema.isMaxLength(2048))),
  note: Schema.optional(Schema.String.check(Schema.isMaxLength(2000))),
  document: Schema.optional(Id),
  /**
   * A file attached in this conversation, by its name: the claims are recorded against
   * that file, which the library already lists, instead of a document added by hand.
   */
  attachment: Schema.optional(Text.check(Schema.isMaxLength(255))),
  /**
   * The story the document is about, by id: one of HeyDay's, so that story can
   * show it beside the outlets' accounts, or one of the library's own.
   */
  story: Schema.optional(Id),
  /** Starts a story of the library's own with this headline, for an event HeyDay does not hold, and files the document under it. */
  storyTitle: Schema.optional(Text.check(Schema.isMaxLength(300))),
  claims: Schema.Array(LibraryClaim).check(Schema.isMaxLength(200)),
});
export const HeyDexLibraryDocumentInput = Schema.Struct({ id: Id });
export const HeyDexLibraryAdded = Schema.Struct({
  document: Text,
  added: Count,
  /** The story the document is filed under, when it has one. */
  story: Schema.optional(Text),
});
/** For each of the user's claims, the public claims that may speak to the same thing. */
export const HeyDexLibraryMatchInput = Schema.Struct({
  document: Schema.optional(Id),
  perClaim: Schema.optional(limit(10)),
});
export const HeyDexLibraryMatches = Schema.Struct({
  /** How many pulled HeyDay claims were compared against. */
  searched: Count,
  /** Whether the user's model compared by meaning as well as by shared words. */
  meaning: Schema.Boolean,
  /** Pulled claims the model has yet to read; ask again to have them compared by meaning too. */
  waiting: Count,
  matches: Schema.Array(
    Schema.Struct({
      claim: Schema.Struct({ id: Text, document: Text, title: Text, text: Text }),
      heyday: Schema.Array(
        Schema.Struct({
          id: Text,
          ref: Text,
          outlet: Schema.String,
          text: Schema.String,
          status: Schema.String,
          publishedAt: Nullable(Schema.String),
          /** The share of the user's claim's words the public claim holds. */
          overlap: Schema.Number,
          match: Schema.Literals(["words", "meaning", "both"]),
        }),
      ),
    }),
  ),
});
export const HeyDexLibrary = Schema.Struct({
  documents: Schema.Array(
    Schema.Struct({
      id: Text,
      ref: Text,
      title: Text,
      path: Nullable(Schema.String),
      url: Nullable(Schema.String),
      note: Nullable(Schema.String),
      /** The HeyDay story the document is about, when one was named. */
      story: Nullable(Schema.String),
      /** The attached file it was read from, when it came from a conversation. */
      attachment: Nullable(Schema.String),
      addedAt: Text,
      claims: Count,
    }),
  ),
  /**
   * The files the user attached, by the conversation they were attached in, newest
   * conversation first. A file is in the library from the moment it is sent;
   * `document` is what has been read out of it so far.
   */
  conversations: Schema.Array(
    Schema.Struct({
      threadId: Text,
      title: Schema.String,
      files: Schema.Array(
        Schema.Struct({
          attachmentId: Text,
          name: Text,
          mimeType: Text,
          sizeBytes: Count,
          image: Schema.Boolean,
          addedAt: Text,
          document: Nullable(Schema.Struct({ id: Text, ref: Text, claims: Count })),
        }),
      ),
    }),
  ),
  /** Every document held; `documents` may list only the newest. */
  documentCount: Count,
  claims: Count,
  /** How many of the library's records the model has read, so meaning can find them. */
  embedded: Count,
  model: Nullable(Schema.Struct({ url: Text, model: Text })),
  /** Why the model could not be used when it was last asked. */
  modelError: Nullable(Schema.String),
});
export type HeyDexLibrary = typeof HeyDexLibrary.Type;

// Tracking. A watch is a standing question over part of the record, kept on the
// user's machine and re-checked against the change feed.

export const HeyDexWatchScope = Schema.Struct(scope);
export const HeyDexWatch = Schema.Struct({
  id: Text,
  threadId: Nullable(Text),
  title: Line,
  question: Schema.String,
  scope: HeyDexWatchScope,
  scopeLabel: Schema.String,
  since: Text,
  createdAt: Text,
  checkedAt: Nullable(Text),
  autoRun: Schema.Boolean,
  changes: Schema.Array(HeydayChange),
  error: Nullable(Schema.String),
});
export type HeyDexWatch = typeof HeyDexWatch.Type;
export const HeyDexWatches = Schema.Struct({ items: Schema.Array(HeyDexWatch) });
export const HeyDexTrackInput = Schema.Struct({
  title: Line,
  question: Schema.optional(Schema.String.check(Schema.isMaxLength(2000))),
  ...scope,
  since: Schema.optional(Instant),
  autoRun: Schema.optional(Schema.Boolean),
});
export const HeyDexWatchInput = Schema.Struct({ id: Text });

export class HeydayUnavailable extends Schema.TaggedError<HeydayUnavailable>()(
  "HeydayUnavailable",
  {
    endpoint: Schema.String,
    status: Nullable(Schema.Int),
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message() {
    return `HeyDay News cannot be reached right now (${this.status === null ? "network failure or timeout" : `status ${this.status}`}). Carry on with other sources and tell the user.`;
  }
}
export class HeydayNotFound extends Schema.TaggedError<HeydayNotFound>()("HeydayNotFound", {
  endpoint: Schema.String,
  status: Schema.Literal(404),
}) {
  override get message() {
    return "This HeyDay News record was not found (status 404).";
  }
}
/** The API refused the request and said why; the reason is written for the caller to act on. */
export class HeydayInvalidRequest extends Schema.TaggedError<HeydayInvalidRequest>()(
  "HeydayInvalidRequest",
  {
    endpoint: Schema.String,
    reason: Schema.String,
  },
) {
  override get message() {
    return `HeyDay News refused this request: ${this.reason}`;
  }
}
export class HeydayUnexpectedResponse extends Schema.TaggedError<HeydayUnexpectedResponse>()(
  "HeydayUnexpectedResponse",
  {
    endpoint: Schema.String,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message() {
    return `Could not read this HeyDay News record: unexpected response from ${this.endpoint}.`;
  }
}
/** A watch, a contribution or a ledger table this installation does not hold. */
export class HeyDexNotFound extends Schema.TaggedError<HeyDexNotFound>()("HeyDexNotFound", {
  what: Schema.String,
}) {
  override get message() {
    return `HeyDex has no ${this.what}.`;
  }
}
/** The local ledger refused a request; the reason is written for the caller to act on. */
export class HeyDexLedgerError extends Schema.TaggedError<HeyDexLedgerError>()(
  "HeyDexLedgerError",
  { reason: Schema.String },
) {
  override get message() {
    return `The local ledger refused this: ${this.reason}`;
  }
}
export const HeydayError = Schema.Union([
  HeydayUnavailable,
  HeydayNotFound,
  HeydayInvalidRequest,
  HeydayUnexpectedResponse,
]);
export type HeydayError = typeof HeydayError.Type;
